import { useEffect, useState, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Eye, Pencil, Printer, Copy, Trash2, Search, FilePlus2, X } from "lucide-react";
import api, { errMsg, inr } from "../lib/api";
import PageHeader from "../components/PageHeader";
import StatusBadge from "../components/StatusBadge";
import WorkStatusBadge from "../components/WorkStatusBadge";
import { useAuth } from "../context/AuthContext";

const WINDOW_LABEL = { all: "All OA", 15: "Last 15 Days OA", 30: "Last 30 Days OA", 90: "Last 3 Months OA", 180: "Last 6 Months OA", 365: "Last 1 Year OA" };

export default function OAHistory() {
  const navigate = useNavigate();
  const [sp] = useSearchParams();
  const windowDays = sp.get("window");
  const workStatusParam = sp.get("workStatus");
  const { user } = useAuth();
  const isAdmin = user.role === "admin";
  const perms = user.permissions || [];
  const can = (p) => isAdmin || perms.includes(p);
  const canStatus = can("change_oa_status");
  const [rows, setRows] = useState([]);
  const [holdModal, setHoldModal] = useState(null);
  const [holdRemark, setHoldRemark] = useState("");
  const [filters, setFilters] = useState({ oaNumber: "", customer: "", poNumber: "", dateFrom: "", dateTo: "", employee: "", status: "" });

  const load = useCallback(async () => {
    const params = Object.fromEntries(Object.entries(filters).filter(([, v]) => v));
    if (windowDays && windowDays !== "all") {
      const d = new Date(); d.setDate(d.getDate() - Number(windowDays));
      params.dateFrom = d.toISOString().slice(0, 10);
    }
    if (workStatusParam) params.workStatus = workStatusParam;
    const { data } = await api.get("/oas", { params });
    setRows(data);
  }, [filters, windowDays, workStatusParam]);

  useEffect(() => { load(); }, [load]);

  const clear = () => setFilters({ oaNumber: "", customer: "", poNumber: "", dateFrom: "", dateTo: "", employee: "", status: "" });

  const duplicate = async (id) => {
    try { const { data } = await api.post(`/oas/${id}/duplicate`); toast.success(`Duplicated as ${data.oaNumber}`); navigate(`/oa/${data.id}/edit`); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const del = async (id, num) => {
    if (!window.confirm(`Delete OA ${num}? This cannot be undone.`)) return;
    try { await api.delete(`/oas/${id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const changeStatus = async (o, ws) => {
    if (ws === "hold") { setHoldModal(o); setHoldRemark(o.holdRemark || ""); return; }
    try { await api.patch(`/oas/${o.id}/status`, { workStatus: ws }); toast.success(`Status → ${ws}`); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const confirmHold = async () => {
    if (!holdRemark.trim()) return toast.error("Hold Remark is mandatory.");
    try { await api.patch(`/oas/${holdModal.id}/status`, { workStatus: "hold", holdRemark }); toast.success("On Hold"); setHoldModal(null); setHoldRemark(""); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };

  const set = (k, v) => setFilters((p) => ({ ...p, [k]: v }));

  return (
    <div>
      <PageHeader title={workStatusParam ? `${workStatusParam.charAt(0).toUpperCase() + workStatusParam.slice(1)} OA` : windowDays ? WINDOW_LABEL[windowDays] || "OA History" : "OA History"} subtitle={`${rows.length} order acceptance(s)`}
        actions={can("create_oa") && <button className="btn-accent" onClick={() => navigate("/oa/new")}><FilePlus2 className="h-4 w-4" /> Create OA</button>} />
      <div className="p-6 lg:p-8 space-y-4">
        <div className="card p-4 grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
          <input className="input" placeholder="OA Number" value={filters.oaNumber} onChange={(e) => set("oaNumber", e.target.value)} data-testid="filter-oa-number" />
          <input className="input" placeholder="Customer" value={filters.customer} onChange={(e) => set("customer", e.target.value)} data-testid="filter-customer" />
          <input className="input" placeholder="PO Number" value={filters.poNumber} onChange={(e) => set("poNumber", e.target.value)} data-testid="filter-po" />
          <input type="date" className="input" value={filters.dateFrom} onChange={(e) => set("dateFrom", e.target.value)} data-testid="filter-date-from" />
          <input type="date" className="input" value={filters.dateTo} onChange={(e) => set("dateTo", e.target.value)} data-testid="filter-date-to" />
          {isAdmin && <input className="input" placeholder="Employee" value={filters.employee} onChange={(e) => set("employee", e.target.value)} data-testid="filter-employee" />}
          <select className="input" value={filters.status} onChange={(e) => set("status", e.target.value)} data-testid="filter-status">
            <option value="">All Status</option><option value="draft">Draft</option><option value="generated">Generated</option>
            <option value="completed">Completed</option><option value="cancelled">Cancelled</option>
          </select>
          <button className="btn-ghost btn-sm col-span-2 md:col-span-1" onClick={clear}><X className="h-4 w-4" /> Clear</button>
        </div>

        <div className="card overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="th">OA No.</th><th className="th">OA Date</th><th className="th">Customer</th>
                  <th className="th">PO No.</th><th className="th">Net Total</th><th className="th">Created By</th>
                  <th className="th">Work Status</th><th className="th">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {rows.length === 0 && <tr><td className="td text-slate-400" colSpan={8}>No matching OA found.</td></tr>}
                {rows.map((o) => (
                  <tr key={o.id} className="hover:bg-slate-50" data-testid={`oa-row-${o.oaNumber}`}>
                    <td className="td font-mono font-semibold text-slate-900">{o.oaNumber}{o.source === "IMPORTED" && <span className="ml-1 text-[9px] text-purple-600 font-bold">IMP</span>}</td>
                    <td className="td">{o.oaDate || "—"}</td>
                    <td className="td">{o.customerName}</td>
                    <td className="td">{o.partyPONumber || "—"}</td>
                    <td className="td font-semibold">{inr(o.netTotal)}</td>
                    <td className="td">{o.createdByName}</td>
                    <td className="td">
                      {canStatus ? (
                        <select className="input py-1 text-xs" value={(o.workStatus || "pending")} onChange={(e) => changeStatus(o, e.target.value)} data-testid={`workstatus-select-${o.oaNumber}`}>
                          <option value="pending">🟡 Pending</option><option value="active">🔵 Active</option>
                          <option value="hold">🟠 Hold</option><option value="completed">🟢 Completed</option>
                        </select>
                      ) : <WorkStatusBadge status={o.workStatus} />}
                      {o.workStatus === "hold" && o.holdRemark && <div className="text-[10px] text-orange-600 mt-0.5 max-w-[160px] truncate" title={o.holdRemark}>{o.holdRemark}</div>}
                    </td>
                    <td className="td">
                      <div className="flex items-center gap-1">
                        <button title="Details" className="p-1.5 rounded hover:bg-slate-100" onClick={() => navigate(`/oa/${o.id}/details`)} data-testid={`details-${o.oaNumber}`}><Eye className="h-4 w-4 text-amber-600" /></button>
                        <button title="View" className="p-1.5 rounded hover:bg-slate-100" onClick={() => navigate(`/oa/${o.id}/preview`)} data-testid={`view-${o.oaNumber}`}><Printer className="h-4 w-4 text-slate-600" /></button>
                        {(isAdmin || (can("edit_oa") && !["generated", "completed"].includes(o.status))) && <button title="Edit" className="p-1.5 rounded hover:bg-slate-100" onClick={() => navigate(`/oa/${o.id}/edit`)} data-testid={`edit-${o.oaNumber}`}><Pencil className="h-4 w-4 text-slate-600" /></button>}
                        <button title="Print/PDF" className="p-1.5 rounded hover:bg-slate-100" onClick={() => navigate(`/oa/${o.id}/preview`)}><Printer className="h-4 w-4 text-slate-600" /></button>
                        {can("duplicate_oa") && <button title="Duplicate" className="p-1.5 rounded hover:bg-slate-100" onClick={() => duplicate(o.id)} data-testid={`duplicate-${o.oaNumber}`}><Copy className="h-4 w-4 text-slate-600" /></button>}
                        {(isAdmin || can("delete_oa")) && <button title="Delete" className="p-1.5 rounded hover:bg-red-50" onClick={() => del(o.id, o.oaNumber)} data-testid={`delete-${o.oaNumber}`}><Trash2 className="h-4 w-4 text-red-600" /></button>}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {holdModal && (
        <div className="fixed inset-0 z-40 bg-black/40 flex items-center justify-center p-4" onClick={() => setHoldModal(null)}>
          <div className="card w-full max-w-md p-6" onClick={(e) => e.stopPropagation()} data-testid="hold-remark-modal">
            <h3 className="font-head text-lg font-bold mb-1">Put OA {holdModal.oaNumber} on Hold</h3>
            <p className="text-sm text-slate-500 mb-4">A Hold Remark is mandatory.</p>
            <textarea className="input min-h-[90px]" placeholder="e.g. Material confirmation pending from customer." value={holdRemark} onChange={(e) => setHoldRemark(e.target.value)} data-testid="hold-remark-input" />
            <div className="flex justify-end gap-2 mt-4">
              <button className="btn-outline" onClick={() => setHoldModal(null)}>Cancel</button>
              <button className="btn-primary" onClick={confirmHold} data-testid="confirm-hold-btn">Confirm Hold</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
